/**
 * **Kube (kognitiv/seekda)**
 *
 * @exports bs_323
 * @category engines
 */
function factory({ window, document, thn, }) {
    const engine = {};
    /**
       * @memberOf module:bs_323
       * @see {@link prestart}
       *
       * @param {object} ctx - The context object.
       */
    engine.boot = function boot() {
        /* PAGE REFRESH OBSERVER
            * All nodes get removed when:
            - Changing language
            - Navigating the funnel back and forth
            * For this reason we observe the main element '#__next' for any node removal
            */
        const mainEl = document.querySelector('#__next');
        if (mainEl && !thn.kubePageObserver) {
            thn.kubePageObserver = thn.debounce(function thnKubePageObserver() {
                thn.languageCheck().then(function ({ changed }) {
                    // We changed language
                    if (changed) {
                        thn.logger.debug('**********  Language changed **********');
                        // We changed funnel page
                    }
                    else {
                        const previousPage = thn.data.pageName;
                        thn.pageCheck();
                        const pageChanged = previousPage !== thn.data.pageName;
                        if (pageChanged) {
                            thn.logger.debug('********** Page changed **********');
                        }
                        // If we are back in R&R reset PW
                        if (thn.data.pageName === 'Rooms and Rates' && pageChanged) {
                            // Try to refresh the PW data
                            if (thn.widgets.price.refresh() && !thn.widgets.price.isSet()) {
                                // Hide PW if data couldn't be set
                                thn.widgets.price.hide();
                            }
                        }
                    }
                });
            }, 150);
            thn.observeDom(mainEl, thn.kubePageObserver, {
                childList: true,
                subtree: false,
                attributes: false,
            });
            thn.shutdown(function () {
                thn.stopObserveDom(mainEl, thn.kubePageObserver);
                delete thn.kubePageObserver;
            });
        }
        const container = document.querySelector('#jupiterx-main');
        if (container) {
            let pageRefresh = thn.debounce(thn.pageCheck, 500);
            thn.observeDom(container, pageRefresh, {
                childList: true,
                subtree: true,
                attributes: true,
            });
            thn.shutdown(function () {
                pageRefresh.cancel();
                thn.stopObserveDom(container, pageRefresh);
                pageRefresh = null;
            });
        }
    };
    /**
       * @memberOf module:bs_323
       * @this thn.widgets.price
       * @see {@link Price#observe_dom}
       *
       * @param {object} ctx - The context object.
       */
    engine.priceObserveDom = function priceObserveDom(ctx) {
        /* NEW SEARCH OBSERVER
            * The page doesn't display a classic loader when
            - changing dates
            - changing occupancy
            - changing currency
            * Instead, the page displays placeholder room elements on the page called 'loading-block-wrapper'
            */
        const roomsEl = document.querySelector('.room-list-wrapper');
        if (roomsEl && !thn.kubePriceObserver) {
            thn.kubePriceObserver = thn.debounce(function thnKubePriceObserver() {
                const loadingBlockEl = document.querySelector('.loading-block-wrapper');
                if (!loadingBlockEl) {
                    thn.logger.debug('********** New search **********');
                    // Try to refresh the PW data
                    if (thn.widgets.price.refresh() && !thn.widgets.price.isSet()) {
                        // Hide PW if data couldn't be set
                        thn.widgets.price.hide();
                    }
                }
            }, 150);
            thn.observeDom(roomsEl, thn.kubePriceObserver, {
                childList: true,
                subtree: false,
                attributes: false,
            });
            thn.pageUnload(function () {
                thn.stopObserveDom(roomsEl, thn.kubePriceObserver);
                delete thn.kubePriceObserver;
            });
        }
        const containerRooms = document.querySelector('.skd-wrapper-results.skd-offer-page');
        if (containerRooms) {
            let refreshRooms = thn.debounce(thn.widgets.price.refresh, 500);
            thn.observeDom(containerRooms, refreshRooms, {
                childList: true,
                subtree: true,
                attributes: true,
            });
            thn.shutdown(function () {
                refreshRooms.cancel();
                thn.stopObserveDom(containerRooms, refreshRooms);
                refreshRooms = null;
            });
        }
    };
    engine._getLastSearch = function () {
        const bookingEngineHistory = window.BookingFlowTracker && window.BookingFlowTracker.tracker && window.BookingFlowTracker.tracker.trackingHistory;
        if (!bookingEngineHistory || !bookingEngineHistory.length) {
            return undefined;
        }
        const bookingEngineEvents = bookingEngineHistory.filter(function getLastSearch(bookingEngineEvent) {
            return bookingEngineEvent.type === 'viewOffers';
        });
        const lastEvent = bookingEngineEvents && bookingEngineEvents[bookingEngineEvents.length - 1];
        const lastEventData = lastEvent?.data && lastEvent?.data?.eventData;
        return lastEventData;
    };
    engine._getOccupancy = function (roomNumber) {
        const lastSearch = engine._getLastSearch();
        const roomArrayIndex = roomNumber - 1;
        const roomsInfo = lastSearch && lastSearch.searchData && lastSearch.searchData.rooms && lastSearch.searchData.rooms.length && lastSearch.searchData.rooms[roomArrayIndex];
        const result = {
            adults: 2,
            children: 0,
            childrenAges: [],
        };
        if (!roomsInfo) {
            return result;
        }
        result.adults = roomsInfo.numberOfAdults;
        result.children = roomsInfo.numberOfChildren;
        for (let i = 0; i <= roomsInfo.actualAges.length; i += 1) {
            if (roomsInfo.actualAges[i] === 0) {
                roomsInfo.actualAges[i] = 10;
            }
        }
        result.childrenAges = roomsInfo.actualAges;
        return result;
    };
    engine._getBestAvailableRate = function () {
        const lastSearch = engine._getLastSearch();
        return lastSearch && lastSearch.minOfferValue;
    };
    /**
       * @memberOf module:bs_323
       * @see {@link language_check}
       *
       * @param {object} ctx - The context object.
       */
    engine.languageCheck = function languageCheck(ctx) {
        return new Promise(function (resolve, reject) {
            const lang = thn.dataLayerHelper.get('bookingFlowData')?.searchData?.languageCode
                || thn.parseUrlParameter('skd-language-code')
                || document.documentElement?.getAttribute('locale');
            if (lang && ctx.langs.test(lang)) {
                resolve(lang);
                return;
            }
            reject('no_language');
        });
    };
    /**
       * @memberOf module:bs_323
       * @this thn.widgets.price
       * @see {@link Price#set_data}
       *
       * @param {object} ctx - The context object.
       */
    engine.priceParseData_v1 = function priceParseData_v1(ctx) {
        const isDerRindererhof = thn.data.propertyId === 1034957;
        const widgetData = {};
        let price;
        if (!window.location.hostname.includes('officialbookings')) {
            return widgetData;
        }
        // DATES
        const startDateFromSearchData = engine._getLastSearch()?.searchData?.startDate;
        const endDateFromSearchData = engine._getLastSearch()?.searchData?.endDate;
        const startDateFromParam = thn.parseUrlParameter('skd-checkin') || thn.parseUrlParameter('checkin');
        const endDateFromParam = thn.parseUrlParameter('skd-checkout') || thn.parseUrlParameter('checkout');
        widgetData.dateBegin = (startDateFromSearchData || startDateFromParam)?.replace(/(\d+)\D+(\d+)\D+(\d+)/, '$1/$2/$3');
        widgetData.dateEnd = (endDateFromSearchData || endDateFromParam)?.replace(/(\d+)\D+(\d+)\D+(\d+)/, '$1/$2/$3');
        // OCCUPANCY
        const roomNumberEl = document.querySelector('.room-item-selector');
        const roomNumber = roomNumberEl && roomNumberEl.textContent && (thn.parseNumber(roomNumberEl.textContent) || 1);
        widgetData.roomsDispo = [engine._getOccupancy(roomNumber)];
        // PRICE
        widgetData.currency = isDerRindererhof ? 'EUR' : (thn.parseUrlParameter('skd-currency-code') || thn.objectGet(window.dataLayer, 'currencyCode') || engine._getLastSearch().searchData?.currencyCode);
        price = engine._getBestAvailableRate();
        const noRoomsEl = document.querySelector('.feedback-message-error, .rooms-main-wrapper > .fully-booked-message');
        if (noRoomsEl) {
            widgetData.noRooms = true;
        }
        else {
            price = engine._getBestAvailableRate();
            const priceType = thn.parseUrlParameter('skd-time-price-base');
            widgetData.price = priceType !== 'perNight' && widgetData.dateBegin && widgetData.dateEnd ? price / thn.daysBetween(widgetData.dateBegin, widgetData.dateEnd) : price;
            widgetData.set = true;
        }
        return widgetData;
    };
    /**
       * @memberOf module:bs_323
       * @this thn.widgets.price
       * @see {@link Price#set_data}
       *
       * @param {object} ctx - The context object.
       */
    engine.priceParseData_v2 = function priceParseData_v2(ctx) {
        const widgetData = {};
        if (window.location.hostname.includes('officialbookings')) {
            return widgetData;
        }
        // DATES
        const date = document.querySelector('.skd-widget-form-control.skd-checkin')?.value.split(' ');
        const dateBegin = date[0]?.replace(/(\d+)\D+(\d+)\D+(\d+)/, '$3/$1/$2');
        const dateEnd = date[2]?.replace(/(\d+)\D+(\d+)\D+(\d+)/, '$3/$1/$2');
        let nights;
        if (dateBegin && dateEnd) {
            widgetData.dateBegin = dateBegin;
            widgetData.dateEnd = dateEnd;
            nights = thn.daysBetween(widgetData.dateBegin, widgetData.dateEnd);
        }
        const roomsElms = [...document.querySelectorAll('.skd-dsr-room-offers')].filter((room) => thn.isVisible(room));
        const roomIndex = thn.parseNumber(roomsElms[0]?.getAttribute('data-room-index')) || 0;
        const adultsValue = document.querySelectorAll('input.skd-adults')[roomIndex]?.value;
        const childrenValue = document.querySelectorAll('input.skd-children ')[roomIndex]?.value;
        const childrenAgesElms = [...document.querySelectorAll('.skd-room-box.skd-room-editor')][roomIndex]?.querySelectorAll('.skd-children-ages-input select');
        const childrenAgesValue = (childrenAgesElms?.length && [...childrenAgesElms]?.map((age) => thn.parseNumber(age?.value))) || [];
        const ratesElms = [...document.querySelectorAll('.skd-widget-col-rate-price .skd-dsr-price-current')].filter((room) => thn.isVisible(room));
        const prices = ratesElms.map((rate) => thn.parseNumber(rate?.textContent));
        const currency = thn.parseCurrency(document.querySelector('.skd-dsr-price')?.textContent);
        widgetData.roomsDispo = [{
                adults: thn.parseNumber(adultsValue) || 2,
                children: thn.parseNumber(childrenValue) || 0,
                childrenAgesValue,
            }];
        if (!thn.empty(prices) && currency && nights) {
            widgetData.price = Math.min(...prices) / nights;
            widgetData.currency = currency;
            widgetData.set = true;
        }
        else if (thn.empty(prices)) {
            widgetData.noRooms = true;
        }
        return widgetData;
    };
    /**
       * @memberOf module:bs_323
       * @this thn.widgets.booking
       * @see {@link Booking#set_data}
       *
       * @param {object} ctx - The context object.
       */
    engine.bookingParseData = function bookingParseData(ctx) {
        const widgetData = {};
        const bookingData = window.next?.router?.query;
        const dataLayerBookingInfo = thn.dataLayerHelper.get('bookingFlowData');
        // BOOKING_ID
        const bookingId = bookingData?.reservationId || dataLayerBookingInfo?.reservationId || document.querySelector('.booking-details-view-details-trigger')?.textContent;
        if (!bookingId || dataLayerBookingInfo?.status?.toLowerCase().includes('cancelled')) {
            widgetData.set = false;
            return widgetData;
        }
        widgetData.bookingId = bookingId;
        // DATES
        const domDateBegin = document.querySelector('.between-dates-start-date')?.textContent;
        const domDateEnd = document.querySelector('.between-dates-end-date')?.textContent;
        widgetData.dateBegin = thn.parseUrlParameter('skd-checkin')?.replace(/(\d+)\D+(\d+)\D+(\d+)/, '$1/$2/$3') || domDateBegin.replace(/(\d+)\D+(\d+)\D+(\d+)/, '$3/$1/$2');
        widgetData.dateEnd = thn.parseUrlParameter('skd-checkout')?.replace(/(\d+)\D+(\d+)\D+(\d+)/, '$1/$2/$3') || domDateEnd.replace(/(\d+)\D+(\d+)\D+(\d+)/, '$3/$1/$2');
        // PRICE
        const priceEl = document.querySelector('.booking-details-total-content');
        const hasPrice = priceEl?.firstElementChild?.textContent;
        widgetData.currency = thn.parseCurrency((hasPrice && thn.parseCurrency(priceEl)) || thn.parseUrlParameter('skd-currency-code') || dataLayerBookingInfo?.currencyCode);
        widgetData.price = thn.parseNumber(priceEl?.firstElementChild?.textContent || dataLayerBookingInfo?.total);
        widgetData.rooms = parseInt(thn.parseUrlParameter('skd-total-rooms') || 1, 10);
        return widgetData;
    };
    return engine;
}

export { factory };
